/**
 * 홈 페이지 - 지도 기반 통합 UI
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { businessAPI, userAPI } from '../services/api';
import UserGuideModal from '../components/UserGuideModal';
import TasteTestModal from '../components/TasteTestModal';
import MapView from '../components/Map/MapView';
import MapBottomSheet from '../components/Map/MapBottomSheet';
import FloatingProfileButton from '../components/FloatingProfileButton';
import FloatingSearchBar from '../components/Map/FloatingSearchBar';
import CategoryFilter from '../components/Map/CategoryFilter';
import { calculateDistance } from '../utils/distance';
import './Home.css';

const HomePage = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('deepfm');
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [showTasteTestModal, setShowTasteTestModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mapRestaurants, setMapRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(20);
  const [userLocation, setUserLocation] = useState(null);
  const [currentBounds, setCurrentBounds] = useState(null);
  const [loadingBusiness, setLoadingBusiness] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const LOAD_MORE_COUNT = 20;
  
  const { user, logout, loading: authLoading } = useAuth();

  // Debounce 검색어 (300ms 지연)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 검색어 변경 시 지도 범위 재로드
  useEffect(() => {
    if (currentBounds) {
      loadMapRestaurants(currentBounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // 카테고리 변경 시 지도 범위 재로드
  useEffect(() => {
    if (currentBounds) {
      loadMapRestaurants(currentBounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // 사용자 위치 가져오기
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.log('위치 권한 거부 또는 오류:', error);
        }
      );
    }
  }, []);

  // 프로필 페이지에서 전달된 businessId 처리
  useEffect(() => {
    const businessId = location.state?.businessId;
    if (businessId && !loadingBusiness && !selectedRestaurant) {
      loadBusinessFromProfile(businessId);
      // state 초기화 (한 번만 실행되도록)
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.businessId]);

  // 프로필에서 전달된 businessId로 가게 정보 로드
  const loadBusinessFromProfile = useCallback(async (businessId) => {
    setLoadingBusiness(true);
    try {
      const response = await businessAPI.get(businessId);
      const business = response.data;
      
      // 거리 계산 추가
      const businessWithDistance = {
        ...business,
        distance: userLocation 
          ? calculateDistance(userLocation.lat, userLocation.lng, business.latitude, business.longitude)
          : null
      };
      
      // 가게 선택
      setSelectedRestaurant(businessWithDistance);
      
    } catch (err) {
      console.error('Error loading business:', err);
      toast.dismiss();
      if (err.response?.status === 404) {
        toast.error('가게를 찾을 수 없습니다.');
      } else {
        toast.error('가게 정보를 불러오는데 실패했습니다.');
      }
    } finally {
      setLoadingBusiness(false);
    }
  }, [userLocation]);

  // selectedRestaurant가 변경되면 지도 중심을 해당 위치로 이동
  const mapCenter = useMemo(() => {
    if (selectedRestaurant && location.state?.businessId) {
      return { lat: selectedRestaurant.latitude, lng: selectedRestaurant.longitude };
    }
    return null;
  }, [selectedRestaurant, location.state?.businessId]);

  // 사용자 인증이 완료된 후 모달 표시 여부 확인
  useEffect(() => {
    // 인증 로딩이 완료되고 사용자 정보가 있을 때만 상태 확인
    if (!authLoading && user) {
      checkModalsToShow();
    }
  }, [authLoading, user]);

  const checkModalsToShow = async () => {
    // 1. 사용법 공지 모달 표시 여부 확인 (세션 기반)
    const userGuideSeen = sessionStorage.getItem('user_guide_seen');
    if (!userGuideSeen) {
      console.log('[사용법 공지 모달] 세션에서 표시되지 않음 - 모달 표시');
      setShowUserGuideModal(true);
      return; // 사용법 공지 모달이 표시되면 취향 테스트 모달은 나중에 확인
    }

    // 2. 사용법 공지 모달이 이미 표시되었거나 건너뛴 경우 취향 테스트 모달 확인
    checkTasteTestModal();
  };

  const checkTasteTestModal = async () => {
    try {
      console.log('[취향 테스트 모달] 사용자 상태 확인 시작');
      const response = await userAPI.getStatus();
      const { should_show_test_popup, is_new_user, has_taste_test, review_count } = response.data;
      
      console.log('[취향 테스트 모달] API 응답:', {
        should_show_test_popup,
        is_new_user,
        has_taste_test,
        review_count
      });
      
      const skipped = sessionStorage.getItem('taste_test_skipped');
      console.log('[취향 테스트 모달] 건너뛰기 상태:', skipped);
      
      if (should_show_test_popup && !skipped) {
        console.log('[취향 테스트 모달] 모달 표시 조건 충족 - 모달 표시');
        setShowTasteTestModal(true);
      } else {
        console.log('[취향 테스트 모달] 모달 표시 조건 불충족:', {
          should_show_test_popup,
          skipped: !!skipped
        });
      }
    } catch (err) {
      console.error('[취향 테스트 모달] 사용자 상태 확인 실패:', {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
    }
  };

  // 사용법 공지 모달 닫기 핸들러
  const handleUserGuideClose = () => {
    setShowUserGuideModal(false);
    // 사용법 공지 모달이 닫힌 후 취향 테스트 모달 확인
    checkTasteTestModal();
  };

  // 지도 범위 기반 레스토랑 로드
  const loadMapRestaurants = useCallback(async (bounds) => {
    setLoading(true);
    setError('');
    setCurrentBounds(bounds);
    
    try {
      const response = await businessAPI.getInBounds({
        north: bounds.north,
        south: bounds.south,
        east: bounds.east,
        west: bounds.west,
        limit: 200,
        search: debouncedSearch || undefined,
        category: selectedCategory || undefined
      });
      
      const { businesses } = response.data;
      
      // 거리 계산 추가
      const businessesWithDistance = businesses.map(b => ({
        ...b,
        distance: userLocation 
          ? calculateDistance(userLocation.lat, userLocation.lng, b.latitude, b.longitude)
          : null
      }));
      
      setMapRestaurants(businessesWithDistance);
      setDisplayedCount(20); // 표시 개수 리셋
      
      if (isInitialLoading) {
        setIsInitialLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load restaurants');
      console.error('Error loading restaurants:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedCategory, userLocation, isInitialLoading]);

  // 지도 bounds 변경 핸들러
  const handleMapBoundsChange = useCallback((bounds) => {
    loadMapRestaurants(bounds);
  }, [loadMapRestaurants]);

  // 더보기 버튼 핸들러
  const handleLoadMore = () => {
    setDisplayedCount(prev => Math.min(prev + LOAD_MORE_COUNT, mapRestaurants.length));
  };

  // 정렬 변경 핸들러
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setDisplayedCount(20); // 리셋
  };

  // 정렬 함수
  const sortRestaurants = useMemo(() => {
    return (restaurants, sortBy) => {
      const sorted = [...restaurants];
      switch(sortBy) {
        case 'deepfm':
          return sorted.sort((a, b) => 
            (b.ai_prediction?.deepfm_rating || b.stars) - 
            (a.ai_prediction?.deepfm_rating || a.stars)
          );
        case 'multitower':
          return sorted.sort((a, b) => 
            (b.ai_prediction?.multitower_rating || b.ai_prediction?.deepfm_rating || b.stars) - 
            (a.ai_prediction?.multitower_rating || a.ai_prediction?.deepfm_rating || a.stars)
          );
        case 'distance':
          return sorted.sort((a, b) => (a.distance || 999) - (b.distance || 999));
        case 'review_count':
          return sorted.sort((a, b) => b.review_count - a.review_count);
        default:
          return sorted;
      }
    };
  }, []);

  // 정렬된 레스토랑 목록
  const sortedRestaurants = useMemo(() => {
    return sortRestaurants(mapRestaurants, sortBy);
  }, [mapRestaurants, sortBy, sortRestaurants]);

  // 레스토랑 선택 핸들러
  const handleRestaurantSelect = (restaurant) => {
    setSelectedRestaurant(restaurant);
  };

  // 레스토랑 선택 해제 핸들러
  const handleRestaurantClose = () => {
    setSelectedRestaurant(null);
  };

  // 검색 핸들러
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // 카테고리 변경 핸들러
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  return (
    <div className="home-container">
      {/* 플로팅 프로필 버튼 */}
      <FloatingProfileButton username={user?.username} onLogout={logout} />

      {/* 플로팅 검색창 */}
      <FloatingSearchBar 
        onSearch={handleSearch}
        placeholder="🔍 음식점 이름, 카테고리, 지역 검색..."
        defaultValue={searchQuery}
      />

      {/* 카테고리 필터 */}
      <CategoryFilter 
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      />

      {/* 지도 (항상 표시) */}
      <MapView 
        restaurants={sortedRestaurants}
        onRestaurantSelect={handleRestaurantSelect}
        onBoundsChange={handleMapBoundsChange}
        loading={loading}
        isInitialLoading={isInitialLoading}
        initialCenter={mapCenter}
        searchQuery={debouncedSearch}
        selectedCategory={selectedCategory}
      />

      {/* 하단 시트 (통합) */}
      <MapBottomSheet 
        restaurants={sortedRestaurants}
        displayedCount={displayedCount}
        onLoadMore={handleLoadMore}
        selectedRestaurant={selectedRestaurant}
        onSelectRestaurant={handleRestaurantSelect}
        onClose={handleRestaurantClose}
        sortBy={sortBy}
        onSortChange={handleSortChange}
      />

      {/* 사용법 공지 모달 */}
      {showUserGuideModal && (
        <UserGuideModal onClose={handleUserGuideClose} />
      )}

      {/* 취향 테스트 모달 */}
      {showTasteTestModal && (
        <TasteTestModal onClose={() => setShowTasteTestModal(false)} />
      )}
    </div>
  );
};

export default HomePage;
